import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Layers, CheckCircle2, XCircle } from 'lucide-react'
import { PageShell } from '@/components/common/PageShell'
import { WorkspaceToolbar } from '@/components/common/WorkspaceToolbar'
import { TableSkeleton } from '@/components/common/TableSkeleton'
import { EmptyState } from '@/components/common/EmptyState'
import { PermissionGate } from '@/components/common/PermissionGate'
import { Button } from '@/components/ui/Button'
import { Toggle } from '@/components/ui/Toggle'
import { useBatchReadiness, useCreateBatch } from '@/features/payroll/hooks/usePayrollBatches'
import { MonthYearPicker } from './MonthYearPicker'
import { StatusBadge } from './StatusBadge'

function defaultMonth(): { year: number; month: number } {
  const now = new Date()
  // If before 15th, default to previous month
  if (now.getDate() < 15) {
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1)
    return { year: prev.getFullYear(), month: prev.getMonth() + 1 }
  }
  return { year: now.getFullYear(), month: now.getMonth() + 1 }
}

export function BatchReadinessPage(): React.ReactElement {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const defaults = defaultMonth()

  const year = Number(searchParams.get('year')) || defaults.year
  const month = Number(searchParams.get('month')) || defaults.month
  const [allowPartial, setAllowPartial] = useState(false)

  const { data: readiness, isLoading } = useBatchReadiness(year, month)
  const createBatch = useCreateBatch()

  const setYear = (y: number): void => {
    setSearchParams({ year: String(y), month: String(month) })
  }
  const setMonth = (m: number): void => {
    setSearchParams({ year: String(year), month: String(m) })
  }

  const entries = readiness ?? []
  const approvedCount = entries.filter((e) => e.runStatus === 'approved').length
  const totalCount = entries.length
  const someButNotAll = approvedCount > 0 && approvedCount < totalCount
  const noneApproved = approvedCount === 0

  const handleCreate = (): void => {
    createBatch.mutate(
      { year, month, allowPartial: someButNotAll ? allowPartial : undefined },
      {
        onSuccess: (batch) => {
          navigate(`/payroll/batches/${batch._id}`)
        },
      },
    )
  }

  return (
    <PageShell
      breadcrumbItems={[
        { label: 'Payroll', to: '/payroll' },
        { label: 'New Batch' },
      ]}
      icon={Layers}
      title="Batch Readiness"
      description="Check company readiness before creating an org payroll batch."
      className="gap-0"
      hideHeader
    >
      <div className="space-y-4">
        <WorkspaceToolbar
          filter={
            <MonthYearPicker year={year} month={month} onYearChange={setYear} onMonthChange={setMonth} hideLabels />
          }
        />

        {isLoading ? (
          <TableSkeleton rows={5} cols={3} />
        ) : entries.length === 0 ? (
          <EmptyState icon={Layers} title="No companies found in this organization" />
        ) : (
          <>
            {/* Summary */}
            <div className="flex items-center gap-3 text-sm">
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {approvedCount} of {totalCount} companies ready
              </span>
              <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                <div
                  className="h-full rounded-full bg-green-500 transition-all"
                  style={{ width: totalCount > 0 ? `${(approvedCount / totalCount) * 100}%` : '0%' }}
                />
              </div>
            </div>

            {/* Readiness table */}
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:border-gray-700 dark:text-gray-400">
                    <th className="px-4 py-3">Company</th>
                    <th className="px-4 py-3">Run Status</th>
                    <th className="px-4 py-3 text-center">Ready</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => (
                    <tr
                      key={entry.companyId}
                      className="cursor-pointer border-b border-gray-100 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/30"
                      onClick={() =>
                        navigate(
                          `/companies/${entry.companyId}/payroll/runs?year=${year}&month=${month}`,
                        )
                      }
                    >
                      <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                        {entry.companyName}
                      </td>
                      <td className="px-4 py-3">
                        {entry.runStatus ? (
                          <StatusBadge status={entry.runStatus} />
                        ) : (
                          <span className="text-gray-400">No run</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {entry.runStatus === 'approved' ? (
                          <CheckCircle2 className="mx-auto h-4 w-4 text-green-500" />
                        ) : (
                          <XCircle className="mx-auto h-4 w-4 text-gray-300 dark:text-gray-500" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Create form */}
            <PermissionGate module="payroll" action="run">
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
                {noneApproved ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No companies have approved runs for this period. Approve company runs first.
                  </p>
                ) : (
                  <div className="flex flex-wrap items-center gap-4">
                    {someButNotAll && (
                      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <Toggle
                          checked={allowPartial}
                          onChange={setAllowPartial}
                        />
                        Allow partial batch ({approvedCount}/{totalCount} companies)
                      </label>
                    )}
                    <Button
                      variant="primary"
                      onClick={handleCreate}
                      disabled={(someButNotAll && !allowPartial)} loading={createBatch.isPending}
                    >
                      Create Batch
                    </Button>
                  </div>
                )}
              </div>
            </PermissionGate>
          </>
        )}
      </div>
    </PageShell>
  )
}

BatchReadinessPage.displayName = 'BatchReadinessPage'
